import React, { useState, useEffect, useRef } from 'react';
import { Select, Button, Space, Typography, message } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { Chain, Residue, extractSequenceFromPDB, extractSequenceFromCIF } from './utils/pdbParser';

const { Text } = Typography;

interface SequenceViewerProps {
  chains?: Chain[];
  pdbData?: string;
  onResidueClick?: (residue: Residue) => void;
  onResidueHover?: (residue: Residue | null) => void;
  selectedResidues?: number[];
  highlightedResidues?: number[];
  height?: number;
  fileName?: string;
  compact?: boolean;
  selectedChainId?: string;
  representation?: string;
  colorScheme?: string;
  onRepresentationChange?: (value: string) => void;
  onColorSchemeChange?: (value: string) => void;
  onSelectedChainChange?: (value: string) => void;
  onResetView?: () => void;
  onScreenshot?: () => void;
  fileOptions?: { value: string; label: string }[];
  selectedFile?: string;
  onSelectedFileChange?: (value: string) => void;
  rightTitle?: string;
  collapsed?: boolean;
  sequenceActive?: boolean;
  onToggleSequence?: () => void;
  showFileSelect?: boolean;
  externalHovered?: { chainId: string; position: number } | null;
}

const SequenceViewer: React.FC<SequenceViewerProps> = ({
  chains: propChains,
  pdbData,
  onResidueClick,
  onResidueHover,
  selectedResidues = [],
  highlightedResidues = [],
  height = 200,
  fileName,
  compact = false,
  selectedChainId,
  representation,
  colorScheme,
  onRepresentationChange,
  onColorSchemeChange,
  onSelectedChainChange,
  onResetView,
  onScreenshot,
  fileOptions,
  selectedFile,
  onSelectedFileChange,
  rightTitle,
  collapsed = false,
  sequenceActive,
  onToggleSequence,
  showFileSelect = true,
  externalHovered = null,
}) => {
  const [chains, setChains] = useState<Chain[]>(propChains || []);
  const [selectedChain, setSelectedChain] = useState<string>('all');
  const [hoveredResidue, setHoveredResidue] = useState<{ id: number; chainId: string } | null>(null);
  const clearHoverTimerRef = useRef<any>(null);

  // 当PDB数据变化时，解析序列
  useEffect(() => {
    if (pdbData) {
      const isCif = (fileName && fileName.toLowerCase().endsWith('.cif')) || /_atom_site\./.test(pdbData);
      const extractedChains = isCif ? extractSequenceFromCIF(pdbData) : extractSequenceFromPDB(pdbData);
      setChains(extractedChains);
    } else if (propChains) {
      setChains(propChains);
    }
  }, [pdbData, propChains, fileName]);

  

  // 复制序列到剪贴板
  const copyText = async (text: string) => {
    try {
      if (typeof navigator !== 'undefined' && (navigator as any).clipboard?.writeText) {
        await (navigator as any).clipboard.writeText(text);
      } else if (typeof document !== 'undefined') {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      message.success('已复制到剪贴板');
    } catch (err) {
      message.error('复制失败');
    }
  };

  

  // 复制当前选择的序列（单链或全部）
  const copyCurrentSequences = () => {
    const effectiveSelectedChain = selectedChainId ?? selectedChain;
    const targets = (effectiveSelectedChain === 'all') ? chains : chains.filter(c => c.id === effectiveSelectedChain);
    if (!targets.length) return;
    const text = targets.map(c => `>Chain ${c.id}\n${c.sequence.map(r => r.code).join('')}`).join('\n');
    copyText(text);
  };

  

  const effectiveSelectedChain = selectedChainId ?? selectedChain;
  const filteredChains = effectiveSelectedChain === 'all' ? chains : chains.filter(c => c.id === effectiveSelectedChain);

  // 残基信息展示交由结构查看器负责（右下角），此处不再生成序列区的悬浮信息

  if (true) {
    const letterWidth = 8;
    const fontSize = 12;
    const gutterWidth = 14; // 左侧ID栏宽度
    const visibleChains = filteredChains.length ? filteredChains : chains;
    const globalMinPos = Math.min(
      ...visibleChains.map(c => (c.sequence.length ? c.sequence[0].position : Number.POSITIVE_INFINITY))
    );
    const globalMaxPos = Math.max(
      ...visibleChains.map(c => (c.sequence.length ? c.sequence[c.sequence.length - 1].position : Number.NEGATIVE_INFINITY))
    );
    const colsCount = Number.isFinite(globalMinPos) && Number.isFinite(globalMaxPos) && globalMaxPos >= globalMinPos
      ? (globalMaxPos - globalMinPos + 1)
      : 0;
    const chainSelectOptions = chains.length
      ? [{ value: 'all', label: 'All' }, ...chains.map(c => ({ value: c.id, label: `Chain ${c.id}` }))]
      : [{ value: 'all', label: 'All' }];
    const fileSelectOptions = (fileOptions && fileOptions.length)
      ? fileOptions
      : [{ value: selectedFile || fileName || 'Model', label: selectedFile || fileName || 'Model' }];
    const renderCompactLine = (chain: Chain, lineIndex: number) => {
      return (
        <div key={chain.id} style={{ whiteSpace: 'nowrap' }}>
          {lineIndex === 0 && (
            <div style={{ fontFamily: 'monospace', height: 18, color: '#999' }}>
              <span style={{ display: 'inline-block', width: gutterWidth }} />
              {Array.from({ length: colsCount }).map((_, idx) => {
                const pos = globalMinPos + idx;
                return (
                  <span key={`n-${pos}`} style={{ display: 'inline-block', width: letterWidth, textAlign: 'center', fontSize: 10 }}>
                    {pos % 10 === 1 ? pos : ''}
                  </span>
                );
              })}
            </div>
          )}
          <div style={{ fontFamily: 'monospace' }}>
            <span
              style={{
                display: 'inline-block',
                width: gutterWidth,
                textAlign: 'center',
                color: '#ff4d4f',
                fontWeight: 600,
                fontSize: 12,
                userSelect: 'none',
                position: 'sticky',
                left: 0,
                background: '#fff',
                borderRight: '1px solid #f0f0f0',
                zIndex: 1
              }}
            >
              {chain.id}
            </span>
            {(() => {
              const posMap = new Map<number, Residue>();
              chain.sequence.forEach(r => posMap.set(r.position, r));
              return Array.from({ length: colsCount }).map((_, idx) => {
                const pos = globalMinPos + idx;
                const residue = posMap.get(pos);
                if (!residue) {
                  return (
                    <span
                      key={`gap-${chain.id}-${pos}`}
                      style={{ display: 'inline-block', width: letterWidth, textAlign: 'center', fontSize }}
                    >
                      {' '}
                    </span>
                  );
                }
                // 判断是否悬停：内部悬停 或 外部（3D结构）悬停
                const isInternalHover = hoveredResidue && hoveredResidue.id === residue.id && hoveredResidue.chainId === chain.id;
                const isExternalHover = externalHovered && externalHovered.position === residue.position && externalHovered.chainId === chain.id;
                const isHover = isInternalHover || isExternalHover;
                
                // 外部悬停使用更明显的样式
                const hoverStyles = isExternalHover ? {
                  background: 'linear-gradient(135deg, #ff4d4f 0%, #ff7875 100%)',
                  transform: 'scale(1.15)',
                  zIndex: 10
                } : isInternalHover ? {
                  background: '#ff4d4f',
                  transform: 'scale(1.1)',
                  zIndex: 5
                } : {
                  background: 'transparent',
                  transform: 'scale(1)',
                  zIndex: 0
                };
                
                return (
                  <span
                    key={`res-${chain.id}-${pos}`}
                    style={{
                      display: 'inline-block',
                      width: letterWidth,
                      textAlign: 'center',
                      fontSize,
                      cursor: 'pointer',
                      color: isHover ? '#fff' : '#4a4a4a',
                      ...hoverStyles,
                      borderRadius: isHover ? 3 : 0,
                      border: isHover ? '1px solid #ff7875' : '1px solid transparent',
                      userSelect: 'none',
                      transition: 'all 0.15s ease',
                      fontWeight: isHover ? 600 : 400,
                      boxShadow: isHover ? '0 2px 6px rgba(255, 77, 79, 0.5)' : 'none',
                      position: 'relative'
                    }}
                    onClick={() => { onResidueClick?.(residue); }}
                    onMouseEnter={() => {
                      if (clearHoverTimerRef.current) {
                        clearTimeout(clearHoverTimerRef.current);
                        clearHoverTimerRef.current = null;
                      }
                      setHoveredResidue({ id: residue.id, chainId: chain.id });
                      onResidueHover?.(residue);
                    }}
                    onMouseLeave={() => {
                      if (clearHoverTimerRef.current) clearTimeout(clearHoverTimerRef.current);
                      clearHoverTimerRef.current = setTimeout(() => {
                        setHoveredResidue(null);
                        onResidueHover?.(null);
                      }, 120);
                    }}
                  >
                    {residue.code}
                  </span>
                );
              });
            })()}
          </div>
        </div>
      );
    };

    return (
      <div style={{ height, position: 'relative' }}>
        {/* Header with file and chain selectors, and controls aligned to right */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          padding: '6px 8px',
          background: '#f5f0e6',
          border: '1px solid #e0d8c6',
          borderRadius: 4,
          marginBottom: 6
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Text style={{ fontSize: 12, color: '#6b6b6b' }}>Sequence of</Text>
            {showFileSelect ? (
              <Select
                value={selectedFile || fileName || (fileSelectOptions[0] && fileSelectOptions[0].value)}
                onChange={(v) => onSelectedFileChange?.(v)}
                options={fileSelectOptions}
                size="small"
                style={{ minWidth: 160 }}
              />
            ) : (
              <Text style={{ fontSize: 12, color: '#262626' }}>
                {selectedFile || fileName || 'Model'}
              </Text>
            )}
            <span style={{ color: '#bfbfbf' }}>•</span>
            <Text style={{ fontSize: 12, color: '#6b6b6b' }}>Chain</Text>
            <Select
              value={effectiveSelectedChain}
              onChange={(v) => { setSelectedChain(v); onSelectedChainChange?.(v); }}
              options={chainSelectOptions}
              size="small"
              style={{ width: 110 }}
            />
          </div>

          <Space size={4}>
            <Select
              value={representation}
              onChange={(v) => onRepresentationChange?.(v)}
              placeholder="表示方式"
              size="small"
              style={{ width: 110 }}
              options={[
                { value: 'cartoon', label: '卡通' },
                { value: 'ball+stick', label: '球棍' },
                { value: 'spacefill', label: '空间填充' },
                { value: 'surface', label: '表面' },
                { value: 'backbone', label: '骨架' },
              ]}
            />
            <Button size="small" onClick={() => onResetView?.()}>重置视图</Button>
            <Button size="small" onClick={() => onScreenshot?.()}>截图</Button>
            <Button size="small" onClick={() => onToggleSequence?.()}>序列</Button>
            <Button size="small" icon={<CopyOutlined />} onClick={copyCurrentSequences}>复制序列</Button>
            {rightTitle && (
              <Text strong style={{ fontSize: 12, color: '#666', marginLeft: 8 }}>{rightTitle}</Text>
            )}
          </Space>
        </div>

        {/* Sequence content with ticks and letters */}
        {!collapsed && (
          <div style={{
            border: '1px solid #e0e0e0',
            borderRadius: 4,
            padding: 8,
            background: '#fff',
            overflowX: 'auto',
            maxHeight: height - 36,
          }}>
            {(filteredChains.length ? filteredChains : chains).map((c, i) => renderCompactLine(c, i))}
          </div>
        )}
      </div>
    );
  }

  
};

export default SequenceViewer;